import {
  EuiButtonIcon,
  EuiPopover,
  EuiContextMenu,
  htmlIdGenerator,
} from "@inscopix/ideas-eui";
import { memo, useState } from "react";
import { ProjectTablesTableColumns } from "./ProjectTablesTable";
import { useProjectTablesTableContext } from "components/ProjectTablesTable/provider/ProjectTablesTableProvider";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";

/*
 * Header menu for data table that displays in the upper right corner
 * Allows for:
 * - setting column visibility
 * - sorting columns
 * - adding tables and folders
 */
export const ProjectTablesTableHeaderMenu = memo(
  function ProjectTablesTableHeaderMenu() {
    const visibleColumnsMap = useProjectTablesTableContext(
      (s) => s.visibleColumnsMap,
    );
    const toggleVisibleColumn = useProjectTablesTableContext(
      (s) => s.toggleVisibleColumn,
    );
    const sortedColumn = useProjectTablesTableContext((s) => s.sortedColumn);
    const onColumnSort = useProjectTablesTableContext((s) => s.onColumnSort);
    const showHidden = useProjectTablesTableContext((s) => s.showHidden);
    const setShowHidden = useProjectTablesTableContext((s) => s.setShowHidden);
    const openCreateDataTableModal = useProjectTablesTableContext(
      (s) => s.openCreateDataTableModal,
    );
    const openCreateAnalysisTableModal = useProjectTablesTableContext(
      (s) => s.openCreateAnalysisTableModal,
    );
    const openCreateFolderModal = useProjectTablesTableContext(
      (s) => s.openCreateFolderModal,
    );

    const canEdit = useProjectPermissionStore((s) => s.permissions.edit);

    const [isHeaderMenuPopoverOpen, setIsHeaderMenuPopoverOpen] =
      useState(false);

    const closePopover = () => {
      setIsHeaderMenuPopoverOpen(false);
    };
    const togglePopover = () => {
      setIsHeaderMenuPopoverOpen((prev) => !prev);
    };

    const handleOpenCreateDataTableModal = () => {
      closePopover();
      openCreateDataTableModal();
    };
    const handleOpenCreateAnalysisTableModal = () => {
      closePopover();
      openCreateAnalysisTableModal();
    };
    const handleOpenCreateFolderModal = () => {
      closePopover();
      openCreateFolderModal();
    };

    const handleShowHiddenToggle = () => {
      setShowHidden(!showHidden);
      closePopover();
    };

    const headerMenuContextPanelShowHideColumnsItems =
      ProjectTablesTableColumns.filter((column) => column.isHidable).map(
        (column) => ({
          name: column.label,
          icon: visibleColumnsMap[column.id] ? "check" : "empty",
          onClick: () => toggleVisibleColumn(column.id),
        }),
      );

    const headerMenuContextPanelSortItems = ProjectTablesTableColumns.filter(
      (column) => column.isSortable,
    ).map((column) => ({
      name: column.label,
      icon:
        column.id === sortedColumn[0]
          ? sortedColumn[1] === "ASC"
            ? "sortUp"
            : "sortDown"
          : "empty",
      onClick: () => onColumnSort(column.id),
    }));

    const headerMenuContextPanels = [
      {
        id: 0,
        width: 220,
        items: [
          {
            name: "Show/hide columns",
            icon: "tableOfContents",
            panel: 1,
          },
          {
            name: "Sort",
            icon: "sortable",
            panel: 2,
          },
          {
            name: "Add",
            icon: "plus",
            panel: 3,
            disabled: !canEdit,
          },
          {
            name: showHidden ? "Hide hidden tables" : "Show hidden tables",
            icon: showHidden ? "eyeClosed" : "eye",
            onClick: handleShowHiddenToggle,
          },
          /*
        // leaving out deletion of selected tables for now
        // may not be clear enough what this applies to re: selection state
        {
          name: "Delete",
          icon: "trash",
        },
        */
        ],
      },
      {
        id: 1,
        title: "Show/Hide Columns",
        items: headerMenuContextPanelShowHideColumnsItems,
      },
      {
        id: 2,
        title: "Sort",
        items: headerMenuContextPanelSortItems,
      },
      {
        id: 3,
        title: "Add",
        items: [
          {
            name: "Data table",
            icon: "tableDensityNormal",
            onClick: handleOpenCreateDataTableModal,
          },
          {
            name: "Analysis table",
            icon: "indexMapping",
            onClick: handleOpenCreateAnalysisTableModal,
          },
          {
            name: "Folder",
            icon: "folderOpen",
            onClick: handleOpenCreateFolderModal,
          },
        ],
      },
    ];

    return (
      <EuiPopover
        id={htmlIdGenerator()()}
        button={
          <EuiButtonIcon
            aria-label={"Table and column menu"}
            iconType="boxesHorizontal"
            onClick={togglePopover}
            data-test-subj="data-table-header-menu-button"
          />
        }
        isOpen={isHeaderMenuPopoverOpen}
        closePopover={closePopover}
        anchorPosition="downLeft"
        css={{ textAlign: "center" }}
        data-test-subj="data-table-header-menu"
        aria-label="Tables menu"
      >
        <EuiContextMenu initialPanelId={0} panels={headerMenuContextPanels} />
      </EuiPopover>
    );
  },
);
